import type {
  ChartConfig,
  ColorRule,
  SemanticColor,
  SeriesColorConfig,
} from '@highereducation/hen-ui';

/**
 * One authored series: what to call it and how to paint it.
 *
 * The color fields are the renderer's own vocabulary, imported as types only.
 * Naming them again here would create a second definition of a shared concept,
 * which is how the two drift.
 */
export interface SeriesPresentation {
  readonly key: string;
  /** Falls back to `key` when absent, so a legend never renders blank. */
  readonly label?: string;
  readonly semanticColor?: SemanticColor | 'auto-by-value' | null;
  readonly colorRules?: ColorRule[];
}

/**
 * Build the series-to-label map a chart draws its legend and tooltip from.
 *
 * Keyed off the series the chart will actually draw rather than the authored
 * list, so a document whose authored keys no longer match the data still
 * describes every series on screen. Entries carry no color: leaving it absent
 * is what lets the renderer assign distinct hues from its categorical palette
 * by series index, which is why most documents never name a color at all.
 */
export function seriesToChartConfig(
  drawnSeries: readonly string[],
  authored: readonly SeriesPresentation[] = [],
): ChartConfig {
  const config: ChartConfig = {};
  for (const key of drawnSeries) {
    const label = authored.find((entry) => entry.key === key)?.label;
    config[key] = { label: label !== undefined && label !== '' ? label : key };
  }
  return config;
}

/**
 * Per-series semantic color and rule overrides.
 *
 * Returns `undefined` when no series carries either, which keeps the chart off
 * its per-point rendering path — that path costs more and only earns it when
 * something actually varies by value.
 */
export function seriesToColorConfigs(
  series: readonly SeriesPresentation[],
): Record<string, SeriesColorConfig> | undefined {
  const configs: Record<string, SeriesColorConfig> = {};
  let any = false;

  for (const entry of series) {
    const hasSemantic =
      entry.semanticColor !== undefined && entry.semanticColor !== null;
    const hasRules =
      entry.colorRules !== undefined && entry.colorRules.length > 0;
    if (!hasSemantic && !hasRules) {
      continue;
    }
    any = true;
    configs[entry.key] = {
      ...(entry.semanticColor !== undefined
        ? { semanticColor: entry.semanticColor }
        : {}),
      ...(hasRules ? { colorRules: entry.colorRules } : {}),
    };
  }

  return any ? configs : undefined;
}
